import { useFormikContext } from 'formik';
import { Box, Typography } from '@mui/material';
import { theme, Icon, Button } from 'bs-unified-ui';
import { useCallback, useState, useEffect } from 'react';
import { Icon as CIcon } from '~/components/common/Icon';
import { useAnnouncementDetailContext } from '../AnnouncementDetailContext';

interface ImageUploadFieldProps {
  name: string;
  label?: string;
}

const ImageUploadField = ({
  name,
  label = 'Upload image',
}: ImageUploadFieldProps) => {
  const { values, setFieldValue, setFieldTouched, validateField } =
    useFormikContext<any>();
  const { announcementDetail } = useAnnouncementDetailContext();

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const fileValue = values[name];
  const imageFileName =
    announcementDetail?.image_file_name || values.image_file_name;
  const displayName =
    fileValue instanceof File ? fileValue.name : imageFileName || fileValue;
  const hasImage = fileValue instanceof File || typeof fileValue === 'string';

  const handleFileSelect = useCallback(
    (file: File | null) => {
      // Reset all errors
      setFileSizeError(null);

      if (!file) {
        setFieldValue(name, null, false);
        setImagePreview(null);
        setFieldTouched(name, true);
        validateField(name);
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setFieldValue(name, null, false);
        setImagePreview(null);
        setFieldTouched(name, true);
        return;
      }

      // Validate file size (1MB = 1048576 bytes)
      const maxSize = 1048576;
      if (file.size > maxSize) {
        setFileSizeError(
          'File size exceeds 1MB. Please choose a smaller file.',
        );
        setFieldValue(name, null, false);
        setImagePreview(null);
        setFieldTouched(name, true);
        return;
      }

      setFieldValue(name, file, false);
      setFieldTouched(name, true);
      validateField(name);

      // Generate preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    },
    [name, setFieldValue, setFieldTouched, validateField],
  );

  useEffect(() => {
    // Handle preview for File object or URL string
    if (!fileValue) {
      setImagePreview(null);
      setFileSizeError(null);
    } else if (fileValue instanceof File) {
      // File object - generate preview from FileReader
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setImagePreview(preview);
      };
      reader.readAsDataURL(fileValue);
    } else if (typeof fileValue === 'string') {
      // URL string from backend
      setImagePreview(fileValue);
    }
  }, [fileValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleRemove = () => {
    setFieldValue(name, null, false);
    setFieldTouched(name, true);
    validateField(name);
  };

  return (
    <Box>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='fs14' color={theme.foreground.black}>
          {label}
        </Typography>
      </Box>

      {fileValue ? (
        <Box>
          {(fileValue instanceof File ||
            typeof fileValue === 'string' ||
            imageFileName) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '4px',
                backgroundColor: '#F4F5F9',
                mb: 1,
                p: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.foreground.black,
                }}
              >
                {displayName}
              </Typography>
              <Button
                variant='text'
                sx={{
                  minWidth: 'unset !important',
                  p: 0,
                }}
              >
                <Icon size={8} name='close' onClick={handleRemove} />
              </Button>
            </Box>
          )}
          {imagePreview && (
            <Box
              sx={{
                backgroundColor: 'white',
                aspectRatio: '2 / 1',
                borderRadius: '4px',
                border: '1px solid #E2E7F0',
                overflow: 'hidden',
                mb: 1,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={imagePreview}
                alt='preview'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            aspectRatio: 2 / 1,
            backgroundColor: '#F4F5F9',
            p: 3,
            borderRadius: '4px',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(!hasImage && {
              border: '1px solid #F24822',
            }),
          }}
        >
          <input
            type='file'
            accept='image/jpeg,image/png'
            onChange={handleInputChange}
            style={{
              display: 'none',
            }}
            id={`${name}-file-input`}
          />
          <label
            htmlFor={`${name}-file-input`}
            style={{
              cursor: 'pointer',
              display: 'block',
            }}
          >
            <CIcon name='upload-cloud-ine' size={32} useBackgroundImg />
            <Typography
              sx={{
                fontSize: 14,
                color: theme.foreground.black,
                mt: 1,
              }}
            >
              Drag and drop file here or{' '}
              <Typography
                component='span'
                sx={{
                  fontSize: 14,
                  color: '#2D91FC',
                  fontWeight: 600,
                }}
              >
                choose file
              </Typography>
            </Typography>
          </label>
        </Box>
      )}

      {!hasImage && (
        <Typography
          sx={{
            fontSize: 12,
            color: '#F24822',
            mt: 0.5,
          }}
        >
          Image is required
        </Typography>
      )}

      {fileSizeError && (
        <Typography
          sx={{
            fontSize: 12,
            color: '#F24822',
            mt: 0.5,
          }}
        >
          {fileSizeError}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            color: 'rgba(51, 54, 61, 0.40)',
          }}
        >
          Supported formats: JPEG, PNG
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: 'rgba(51, 54, 61, 0.40)',
          }}
        >
          Maximum size: 1 MB
        </Typography>
      </Box>
    </Box>
  );
};

export default ImageUploadField;
